/**
 * Version Checker Utility
 * Checks for new app versions and prompts users to update
 */

const VERSION_CHECK_INTERVAL = 60 * 60 * 1000; // Check every hour
const VERSION_KEY = 'app_version';
const LAST_CHECK_KEY = 'last_version_check';

export interface VersionInfo {
  version: string;
  buildTime: string;
  hasUpdate: boolean;
}

/**
 * Get current app version from meta tag or package
 */
export function getCurrentVersion(): string {
  // Try to get from meta tag first
  const metaVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
  if (metaVersion) return metaVersion;
  
  // Fallback to timestamp-based version
  return import.meta.env.VITE_APP_VERSION || new Date().toISOString().split('T')[0];
}

/**
 * Check if a new version is available
 */
export async function checkForUpdates(): Promise<boolean> {
  try {
    const currentVersion = getCurrentVersion();
    const storedVersion = localStorage.getItem(VERSION_KEY);
    
    // If no stored version, store current and return false
    if (!storedVersion) {
      localStorage.setItem(VERSION_KEY, currentVersion);
      return false;
    }
    
    // Check if version has changed
    if (storedVersion !== currentVersion) {
      console.log(`New version detected: ${currentVersion} (was ${storedVersion})`);
      return true;
    }
    
    // Also check by fetching a version file or checking build hash
    const response = await fetch('/version.json?' + Date.now(), {
      cache: 'no-cache'
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.version && data.version !== storedVersion) {
        console.log(`New version available: ${data.version}`);
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking for updates:', error);
    return false;
  }
}

/**
 * Update stored version
 */
export function updateStoredVersion(): void {
  const currentVersion = getCurrentVersion();
  localStorage.setItem(VERSION_KEY, currentVersion);
  localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
}

/**
 * Check if it's time to check for updates
 */
export function shouldCheckForUpdates(): boolean {
  const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
  if (!lastCheck) return true;
  
  const timeSinceLastCheck = Date.now() - parseInt(lastCheck);
  return timeSinceLastCheck > VERSION_CHECK_INTERVAL;
}

/**
 * Initialize version checking
 */
export function initVersionChecker(onUpdateAvailable: () => void): () => void {
  let intervalId: number;
  
  const check = async () => {
    if (shouldCheckForUpdates()) {
      const hasUpdate = await checkForUpdates();
      if (hasUpdate) {
        onUpdateAvailable();
      }
    }
  };
  
  // Check immediately
  check();
  
  // Then check periodically
  intervalId = window.setInterval(check, VERSION_CHECK_INTERVAL);
  
  // Return cleanup function
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
  };
}
